import { datadog } from '@/util/init/datadog'
import type { CanvasLoadReport } from '@/util/canvas-load-timing'
import { getSaveTelemetry } from '@/util/canvas-save-telemetry'

/** After a settled canvas load (idle): active editors should stay at or below this count. */
export const EDITOR_IDLE_ACTIVE_MAX = 2
/** Peak editors while the user is editing during / after load. */
export const EDITOR_INTERACTING_PEAK_MAX = 10

let editorBudgetWarned = false

function readSampleRate(): number {
  const raw = import.meta.env.VITE_CANVAS_LOAD_DD_SAMPLE_RATE
  if (raw === undefined || raw === '') return 0
  const n = Number(raw)
  if (!Number.isFinite(n)) return 0
  return Math.min(1, Math.max(0, n))
}

/** Parsed once at module load; changing env at runtime is not supported. */
const DATADOG_SAMPLE_RATE = readSampleRate()

function traceId(): string | null {
  try {
    return localStorage.getItem('traceId')
  } catch {
    return null
  }
}

/** Numbers and flags only — never document HTML, titles, or user identifiers beyond the trace id. */
export function buildCanvasLoadTelemetryPayload(report: CanvasLoadReport): Record<string, unknown> {
  const saves = getSaveTelemetry()
  return {
    event: 'canvas_load',
    kind: report.kind,
    complete: report.complete,
    hardLoad: report.hardLoad,
    staticFirstRender: report.staticFirstRender,
    sinceStartInteractiveMs: report.sinceStart.interactive ?? null,
    sinceStartAllMountedMs: report.sinceStart['all-mounted'] ?? null,
    longTaskCount: report.longTasks.count,
    longTaskMaxMs: report.longTasks.maxMs,
    editorsCreated: report.editors.created,
    editorsActive: report.editors.active,
    editorsPeak: report.editors.peak,
    domNodes: report.domNodes,
    paintEstimated: report.paintEstimated,
    hiddenDuringLoad: report.hiddenDuringLoad,
    saveParked: saves.parked,
    saveFailed: saves.failed,
    saveRetries: saves.retries,
    traceId: traceId()
  }
}

export function maybeWarnEditorBudget(report: CanvasLoadReport): void {
  if (!import.meta.env.DEV || editorBudgetWarned || !report.complete) return
  const violations: string[] = []
  if (report.editors.active > EDITOR_IDLE_ACTIVE_MAX) {
    violations.push(`idle active ${report.editors.active} > ${EDITOR_IDLE_ACTIVE_MAX}`)
  }
  if (report.editors.peak > EDITOR_INTERACTING_PEAK_MAX) {
    violations.push(`interacting peak ${report.editors.peak} > ${EDITOR_INTERACTING_PEAK_MAX}`)
  }
  if (!violations.length) return
  editorBudgetWarned = true
  console.warn(`[canvas-load] editor budget exceeded: ${violations.join('; ')}`, {
    editors: report.editors,
    staticFirstRender: report.staticFirstRender
  })
}

export function maybeLogCanvasLoadToDatadog(report: CanvasLoadReport): void {
  if (DATADOG_SAMPLE_RATE <= 0 || !datadog) return
  if (!report.complete || report.hiddenDuringLoad || report.paintEstimated) return
  if (Math.random() >= DATADOG_SAMPLE_RATE) return
  try {
    datadog.logger.info('canvas_load', buildCanvasLoadTelemetryPayload(report))
  } catch {
    // Telemetry must never affect the canvas
  }
}

export function onCanvasLoadFinished(report: CanvasLoadReport): void {
  maybeWarnEditorBudget(report)
  maybeLogCanvasLoadToDatadog(report)
}

export function resetCanvasLoadTelemetryForTests(): void {
  editorBudgetWarned = false
}
